import { useState } from 'preact/hooks';
import { api, mensagemDoErro } from '../api';
import { useApi } from '../hooks';
import type { EixoInfo, Secretaria } from '../types';
import { Campo, Carregando, ErroForm, Modal, Selo, avisar } from '../ui';
import { useRecarga } from './comum';

export function SecretariasPage() {
  const [recarga, recarregar] = useRecarga();
  const c = useApi<Secretaria[]>('/secretarias', recarga);
  const eixos = useApi<EixoInfo[]>('/indicadores/eixos');
  const nomeEixo = new Map((eixos.dados ?? []).map((e) => [e.id, e]));
  const [editando, setEditando] = useState<Secretaria | 'nova' | null>(null);
  const lista = c.dados ?? [];

  return (
    <main class="pagina">
      <header class="pagina-topo">
        <div>
          <h1>Secretarias</h1>
          <p class="dica">Quem atua no governo. A cor identifica a secretaria nos gráficos e no mapa.</p>
        </div>
        <button type="button" class="botao primario" onClick={() => setEditando('nova')}>+ Nova secretaria</button>
      </header>

      {c.erro && <ErroForm texto={mensagemDoErro(c.erro)} />}
      {c.carregando && !c.dados && <Carregando />}

      <div class="tabela-rolagem">
        <table class="tabela">
          <caption class="so-leitor">Lista de secretarias</caption>
          <thead><tr><th scope="col">Sigla</th><th scope="col">Nome</th><th scope="col">Eixo</th><th scope="col">Cor</th><th scope="col">Situação</th><th scope="col"><span class="so-leitor">Ações</span></th></tr></thead>
          <tbody>
            {lista.map((s) => (
              <tr key={s.id} class={s.ativa ? '' : 'cancelada'}>
                <td><strong>{s.sigla}</strong></td>
                <td>{s.nome}</td>
                <td>{s.eixoId && nomeEixo.get(s.eixoId) ? <><i class="ponto" style={{ background: nomeEixo.get(s.eixoId)!.cor }} />{nomeEixo.get(s.eixoId)!.nome}</> : <span class="dica">—</span>}</td>
                <td><i class="ponto" style={{ background: s.cor }} />{s.cor}</td>
                <td><Selo texto={s.ativa ? 'Ativa' : 'Inativa'} tom={s.ativa ? 'verde' : 'cinza'} /></td>
                <td class="acoes-linha"><button type="button" class="link" onClick={() => setEditando(s)}>Editar</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {!c.carregando && lista.length === 0 && !c.erro && (
          <div class="vazio-grande">
            <p>Nenhuma secretaria cadastrada ainda. Comece por aqui: sem secretaria não há projetos nem ações.</p>
            <button type="button" class="botao primario" onClick={() => setEditando('nova')}>Cadastrar a primeira secretaria</button>
          </div>
        )}
      </div>

      {editando && <SecretariaForm secretaria={editando === 'nova' ? null : editando} onFechar={() => setEditando(null)} onSalvo={() => { setEditando(null); recarregar(); }} />}
    </main>
  );
}

function SecretariaForm({ secretaria, onFechar, onSalvo }: { secretaria: Secretaria | null; onFechar: () => void; onSalvo: () => void }) {
  const [sigla, setSigla] = useState(secretaria?.sigla ?? '');
  const [nome, setNome] = useState(secretaria?.nome ?? '');
  const [cor, setCor] = useState(secretaria?.cor ?? '#2563eb');
  const [ativa, setAtiva] = useState(secretaria?.ativa ?? true);
  const [eixoId, setEixoId] = useState(secretaria?.eixoId ?? '');
  const eixos = useApi<EixoInfo[]>('/indicadores/eixos');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  async function salvar(e: Event) {
    e.preventDefault();
    setErro('');
    setSalvando(true);
    try {
      if (secretaria) await api(`/secretarias/${secretaria.id}`, { method: 'PATCH', body: { nome: nome.trim(), cor, ativa, eixoId: eixoId || null } });
      else await api('/secretarias', { method: 'POST', body: { sigla: sigla.trim(), nome: nome.trim(), cor, eixoId: eixoId || null } });
      avisar(secretaria ? 'Secretaria atualizada.' : 'Secretaria cadastrada.');
      onSalvo();
    } catch (err) {
      setErro(mensagemDoErro(err));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal titulo={secretaria ? 'Editar secretaria' : 'Nova secretaria'} onFechar={onFechar} pequeno>
      <form class="form" onSubmit={salvar}>
        <Campo rotulo="Sigla *" dica={secretaria ? 'A sigla não pode ser alterada.' : 'De 2 a 20 caracteres. Fica em maiúsculas. Ex.: SEDUC'}>
          {(id) => <input id={id} required minLength={2} maxLength={20} disabled={!!secretaria} value={sigla} onInput={(e) => setSigla(e.currentTarget.value)} />}
        </Campo>
        <Campo rotulo="Nome *">{(id) => <input id={id} required minLength={3} maxLength={150} value={nome} onInput={(e) => setNome(e.currentTarget.value)} />}</Campo>
        <Campo rotulo="Eixo de política pública" dica="Liga as ações desta secretaria aos indicadores oficiais do mesmo eixo (tela Impacto). Ex.: SEDUC → Educação.">
          {(id) => (
            <select id={id} value={eixoId} onChange={(e) => setEixoId(e.currentTarget.value)}>
              <option value="">Nenhum</option>
              {(eixos.dados ?? []).map((x) => <option key={x.id} value={x.id}>{x.nome}</option>)}
            </select>
          )}
        </Campo>
        <Campo rotulo="Cor">{(id) => <input id={id} type="color" value={cor} onInput={(e) => setCor(e.currentTarget.value)} class="cor" />}</Campo>
        {secretaria && (
          <label class="check"><input type="checkbox" checked={ativa} onChange={(e) => setAtiva(e.currentTarget.checked)} /> Secretaria ativa</label>
        )}
        <ErroForm texto={erro} />
        <div class="modal-acoes">
          <button type="button" class="botao" onClick={onFechar}>Cancelar</button>
          <button type="submit" class="botao primario" disabled={salvando}>{salvando ? 'Salvando…' : 'Salvar'}</button>
        </div>
      </form>
    </Modal>
  );
}
